import { type ValidationError, groupErrorsByTarget } from '@wix/form-validator';
import React, { useCallback, useMemo } from 'react';
import { useExternalData } from '../../contexts/external-data-context';
import { useFormFields } from '../../contexts/form-fields-context';
import {
  type OnFieldChange,
  type FormValues,
  type InputField as InputFieldEntity,
  type PossibleValues,
} from '../../types';
import { type FieldLayoutComponent } from '../../../../field-layout';
import { useForm } from '../../../../contexts/form-context';
import { FieldPropsContext } from '../../../../contexts/field-props-context';
import { FIELD_PROPS_MAPPERS } from '../../../../mappers/public-props-map';

export interface NestedFormFieldProps {
  field: InputFieldEntity;
  errors: ValidationError[];
  values: FormValues;
  onFieldChange?: OnFieldChange;
  FieldLayout: FieldLayoutComponent;
}

export const NestedFormField = ({
  field,
  errors,
  values,
  onFieldChange,
  FieldLayout,
}: NestedFormFieldProps) => {
  const componentsById = useFormFields();
  const externalData = useExternalData();
  const form = useForm();

  const fieldValue = values[field.target];
  const errorsByTarget = useMemo(() => groupErrorsByTarget(errors), [errors]);
  const fieldErrors = errorsByTarget?.[field.target];
  const fieldError = fieldErrors?.[0];
  const { requiredIndicator, requiredIndicatorPlacement } =
    form.requiredIndicatorProperties || {};
  const propsMapper = FIELD_PROPS_MAPPERS[field.fieldType];

  const handleChange = useCallback(
    (value: PossibleValues) => {
      if (!value) {
        value = null;
      }

      onFieldChange?.(field.target, value);
    },
    [onFieldChange, field],
  );

  const FieldComponent = componentsById[field.fieldType];

  if (!FieldComponent) {
    return null;
  }

  return (
    <FieldPropsContext.Provider
      value={{
        ...propsMapper({
          ...field.properties,
          id: field.id,
          target: field.target,
          path: field.path,
          error: fieldError,
          errors: fieldErrors,
          value: fieldValue,
          externalData,
          fieldType: field.fieldType,
          requiredIndicatorType: requiredIndicator,
          requiredIndicatorPlacement,
        }),
        onChange: handleChange,
      }}
    >
      <FieldComponent
        {...field.properties}
        onChange={handleChange}
        error={fieldError}
        errors={fieldErrors}
        value={fieldValue}
        id={field.id}
        target={field.target}
        path={field.path}
        externalData={externalData}
        fieldType={field.fieldType}
        FieldLayout={FieldLayout}
        requiredIndicatorType={requiredIndicator}
        requiredIndicatorPlacement={requiredIndicatorPlacement}
      />
    </FieldPropsContext.Provider>
  );
};
